import { cn } from '@/lib/utils';
import { CCSBodyPickerQuestionIds } from '@chiroup/core/constants/CCSQuestionIds';
import { Genders } from '@chiroup/core/enums/Genders.enum';
import { OpenClosedStates } from '@chiroup/core/enums/OpenClosedStates.enum';
import {
  bodyParts,
  defaultBodyParts,
  getPartsSelectedExtra,
} from '@chiroup/core/functions/bodyData';
import { RequestStep } from '@chiroup/core/types/Request.type';
import {
  BodyParts,
  LeftOrRight,
  Part,
  Side,
  SourceOfPain,
  SurveyQuestion,
} from '@chiroup/core/types/Survey.type';
import { Transition } from '@headlessui/react';
import { CircleHelp as QuestionMarkCircleIcon } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ConfirmModal } from '../ConfirmModal';
import maleBack from '../images/male-back.png';
import maleFront from '../images/male-front.png';

const questionContentVerbiage = {
  en: {
    next: 'Next',
    noneOfTheAbove: 'None of the above',
    rotate: 'Rotate',
    back: 'Back',
  },
  es: {
    next: 'Continuar',
    noneOfTheAbove: 'Ninguna de las anteriores',
    rotate: 'Girar',
    back: 'Atrás',
  },
};

type Props = {
  show: boolean;
  setIsSubmitting: (val: boolean) => void;
  isSubmitting: boolean;
  submitSurvey: (
    questions: SurveyQuestion[],
    kiosk: boolean,
    spanishQuestions?: SurveyQuestion[],
    ccsFollowupSurveyIds?: string[],
    validationCode?: string | number
  ) => Promise<any>;
  complete: () => void;
  kiosk: boolean;
  markSurveyComplete: () => void;
  setSubmitText: (val: string) => void;
  submitVerbiage: {
    more: { en: string; es: string };
    done: { en: string; es: string };
  };
  validationCode: string | number;
  surveyIds?: string[];
  ccsFollowups?: string[];
  bodyPickerSubmit: (
    parts: BodyParts,
    sourceOfPain: SourceOfPain,
    spanishQuestions: SurveyQuestion[],
    questions: SurveyQuestion[],
    kiosk: boolean,
    language: string,
    currentQuestion: SurveyQuestion,
    setIsSubmitting: (val: boolean) => void,
    isSubmitting: boolean,
    submitSurvey: (
      questions: SurveyQuestion[],
      kiosk: boolean,
      spanishQuestions?: SurveyQuestion[],
      ccsFollowupSurveyIds?: string[],
      validationCode?: string | number
    ) => Promise<any>,
    complete: () => void,
    markSurveyComplete: () => void,
    setSubmitText: (val: string) => void,
    submitVerbiage: {
      more: { en: string; es: string };
      done: { en: string; es: string };
    },
    setSpanishQuestions: React.Dispatch<React.SetStateAction<SurveyQuestion[]>>,
    setQuestions: React.Dispatch<React.SetStateAction<SurveyQuestion[]>>,
    setCurrentQuestion: (val: SurveyQuestion) => void,
    validationCode: string | number,
    additionalComplaint: boolean,
    isProvider?: boolean,
    aaWI?: string,
    surveyIds?: string[],
    ccsFollowups?: string[],
    updateRequest?: (
      data: {
        prMap: { [key: string]: string };
        steps: RequestStep[];
        language: 'en' | 'es';
        gender: Genders;
        age: number;
        id: string;
      },
      callback?: () => void
    ) => void,
    startingProblemArea?: number,
    abbreviatedCCS?: boolean,
    remainingSteps?: number[],
    customQuestions?: SurveyQuestion[],
    allowAdditionalComplaints?: boolean,
    saveDraft?: (
      questions: SurveyQuestion[],
      spanishQuestions: SurveyQuestion[],
      force?: boolean
    ) => Promise<void>
  ) => void | Promise<any>;
  language?: 'en' | 'es';
  id?: string;
  primaryColor?: string;
  currentQuestion: SurveyQuestion;
  spanishQuestions: SurveyQuestion[];
  questions: SurveyQuestion[];
  setSpanishQuestions: React.Dispatch<React.SetStateAction<SurveyQuestion[]>>;
  setQuestions: React.Dispatch<React.SetStateAction<SurveyQuestion[]>>;
  setCurrentQuestion: (value: SurveyQuestion) => void;
  aaWI?: string;
  updateRequest?: (
    data: {
      prMap: { [key: string]: string };
      steps: RequestStep[];
      language: 'en' | 'es';
      gender: Genders;
      age: number;
      id: string;
    },
    callback?: () => void
  ) => void;
  startingProblemArea?: number;
  myScreen?: boolean;
  additionalComplaints?: boolean;
  abbreviatedCCS?: boolean;
  doNotAllowUnrelatedRegions?: boolean;
  goBack?: () => void;
  customQuestions?: SurveyQuestion[];
  allowAdditionalComplaints?: boolean;
  saveDraft?: (
    questions: SurveyQuestion[],
    spanishQuestions: SurveyQuestion[],
    force?: boolean
  ) => Promise<void>;
};

const Body = ({
  show,
  bodyPickerSubmit,
  language = 'en',
  id,
  primaryColor,
  currentQuestion,
  spanishQuestions,
  questions,
  setSpanishQuestions,
  setQuestions,
  setCurrentQuestion,
  isSubmitting,
  setIsSubmitting,
  aaWI,
  submitSurvey,
  complete,
  markSurveyComplete,
  setSubmitText,
  submitVerbiage,
  validationCode,
  surveyIds,
  ccsFollowups,
  updateRequest,
  startingProblemArea,
  kiosk,
  myScreen = false,
  additionalComplaints = false,
  abbreviatedCCS = false,
  doNotAllowUnrelatedRegions = false,
  goBack,
  customQuestions,
  allowAdditionalComplaints = true,
  saveDraft,
}: Props) => {
  const [genderSelected] = useState<Genders>(Genders.male);
  const [sideSelected, setSideSelected] = useState<Side>('rear');
  const [selected, setSelected] = useState(defaultBodyParts);
  const [confirmContiguousModal, setConfirmContiguousModal] = useState(OpenClosedStates.Closed);
  const [sourceOfPain, setSourceOfPain] = useState<SourceOfPain & { reset: boolean }>({
    side: null,
    x: null,
    y: null,
    reset: false,
  });

  const selectPart = (side: Side, part: Part, leftOrRight: LeftOrRight) => {
    setSelected((prev: any) => ({
      ...prev,
      [side]: {
        ...prev[side],
        [part]: {
          ...prev[side][part],
          [leftOrRight]: !prev[side][part][leftOrRight],
        },
      },
    }));
  };

  useEffect(() => {
    setQuestions((prev: SurveyQuestion[]) => {
      const primaryQuestion = prev.find(q => q.id === CCSBodyPickerQuestionIds.primary);

      if (primaryQuestion) {
        primaryQuestion.problemArea = startingProblemArea ? startingProblemArea + 1 : 1;
      }

      return prev;
    });
  }, [startingProblemArea, setQuestions]);

  useEffect(() => {
    const someAreSelected = Object.values(selected).some(parts =>
      Object.values(parts).some(sides => sides.left || sides.right)
    );

    if (!someAreSelected) {
      setSourceOfPain({ side: null, x: null, y: null, reset: false });
    }
  }, [selected]);

  const turnOver = () => {
    setSideSelected(prev => (prev === 'front' ? 'rear' : 'front'));
  };

  const getConnectedParts = useCallback(
    (
      parts: {
        frontOrBack: Side;
        part: Part;
        leftOrRight: LeftOrRight;
        partString: string;
        connectedParts: string[];
      }[],
      connectedParts: {
        frontOrBack: Side;
        part: Part;
        leftOrRight: LeftOrRight;
        partString: string;
        connectedParts: string[];
      }[] = []
    ): {
      frontOrBack: Side;
      part: Part;
      leftOrRight: LeftOrRight;
      partString: string;
      connectedParts: string[];
    }[] => {
      // We haven't started yet, so lets add the first part and get started...
      if (parts?.length && !connectedParts.length) {
        return getConnectedParts(parts, [parts[0]]);
      } else {
        const partsThatAreNotConnectedYet = parts.filter(
          part =>
            !connectedParts.some(connectedPart => {
              return part.partString === connectedPart.partString;
            })
        );

        if (!partsThatAreNotConnectedYet?.length) {
          return connectedParts;
        }

        const partsConnectedToTheConnectedParts = partsThatAreNotConnectedYet.filter(part => {
          return connectedParts.some(connectedPart => {
            return part.connectedParts.includes(connectedPart.partString);
          });
        });

        if (!partsConnectedToTheConnectedParts?.length) {
          return connectedParts;
        }

        return getConnectedParts(parts, connectedParts.concat(partsConnectedToTheConnectedParts));
      }
    },
    []
  );

  const areAllPartsContiguous = useCallback(
    (
      parts: {
        frontOrBack: Side;
        part: Part;
        leftOrRight: LeftOrRight;
        partString: string;
        connectedParts: string[];
      }[]
    ): boolean => {
      const partsThatAreContiguous = getConnectedParts(parts);

      return partsThatAreContiguous?.length === parts?.length;
    },
    [getConnectedParts]
  );

  const submit = useCallback(() => {
    bodyPickerSubmit(
      selected,
      {
        side: sourceOfPain.side,
        x: sourceOfPain.x,
        y: sourceOfPain.y,
      },
      spanishQuestions,
      questions,
      kiosk,
      language,
      currentQuestion,
      setIsSubmitting,
      isSubmitting,
      submitSurvey,
      complete,
      markSurveyComplete,
      setSubmitText,
      submitVerbiage,
      setSpanishQuestions,
      setQuestions,
      setCurrentQuestion,
      validationCode,
      additionalComplaints,
      myScreen,
      aaWI,
      surveyIds,
      ccsFollowups,
      updateRequest,
      startingProblemArea,
      abbreviatedCCS,
      undefined, // remainingSteps
      customQuestions,
      allowAdditionalComplaints,
      saveDraft
    );
  }, [
    bodyPickerSubmit,
    selected,
    sourceOfPain.side,
    sourceOfPain.x,
    sourceOfPain.y,
    spanishQuestions,
    questions,
    kiosk,
    language,
    currentQuestion,
    setIsSubmitting,
    isSubmitting,
    submitSurvey,
    complete,
    markSurveyComplete,
    setSubmitText,
    submitVerbiage,
    setSpanishQuestions,
    setQuestions,
    setCurrentQuestion,
    validationCode,
    additionalComplaints,
    myScreen,
    aaWI,
    surveyIds,
    ccsFollowups,
    updateRequest,
    startingProblemArea,
    abbreviatedCCS,
    customQuestions,
    allowAdditionalComplaints,
    saveDraft,
  ]);

  const onSubmit = useCallback(() => {
    const hasSelectedParts = Object.values(selected).some(parts =>
      Object.values(parts).some(side => side.left === true || side.right === true)
    );

    if (!hasSelectedParts) {
      return;
    }

    if (id !== CCSBodyPickerQuestionIds.tertiary) {
      const partsSelected = getPartsSelectedExtra(selected);
      const allContiguous = areAllPartsContiguous(partsSelected);

      if (!allContiguous) {
        return setConfirmContiguousModal(OpenClosedStates.Open);
      }
    }

    submit();
  }, [id, submit, selected, areAllPartsContiguous]);

  const disabled = useMemo(() => {
    return !Object.values(selected).some(whatever =>
      Object.values(whatever).some(part => part.left === true || part.right === true)
    );
  }, [selected]);

  const onClickSourceOfPain = (body: string, side: 'front' | 'rear', e: any) => {
    if (!sourceOfPain.side) {
      const elem = document.getElementById(body);
      const rect = elem?.getBoundingClientRect();

      if (rect) {
        const actualBodyHeight = elem?.clientHeight;
        const scale = 1200 / (actualBodyHeight || 1200);
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        setSourceOfPain({
          side,
          x: x * scale,
          y: y * scale,
          reset: false,
        });
      }
    }
  };

  const repositionSourceOfPain = () => {
    setSourceOfPain({
      side: null,
      x: null,
      y: null,
      reset: true,
    });
  };

  useEffect(() => {
    const sheetToBeRemoved = document.getElementById('slider-styles');
    const sheetParent = sheetToBeRemoved?.parentNode;

    if (sheetParent) {
      sheetParent.removeChild(sheetToBeRemoved);
    }

    const sheet = document.createElement('style');
    sheet.setAttribute('id', 'slider-styles');
    sheet.innerHTML = `
          @keyframes tronFilterHover {
              0% {
                  -webkit-filter: drop-shadow(-0.75px 0px 6px ${primaryColor});
                  filter: drop-shadow(-0.75px 0px 6px ${primaryColor});
                  stroke: ${primaryColor};
              }
              100% {
                  -webkit-filter: drop-shadow(-0.75px 0px 6px ${primaryColor});
                  filter: drop-shadow(-0.75px 0px 6px ${primaryColor});
                  stroke: ${primaryColor};
              }
          }

          @keyframes tronFilter {
              0% {
                  -webkit-filter: drop-shadow(-0.75px 0px 6px ${primaryColor});
                  filter: drop-shadow(-0.75px 0px 6px ${primaryColor});
                  stroke: ${primaryColor};
              }
              100% {
                  -webkit-filter: drop-shadow(-0.75px 0px 6px ${primaryColor});
                  filter: drop-shadow(-0.75px 0px 6px ${primaryColor});
                  stroke: ${primaryColor};
              }
          }
          `;
    document.body.appendChild(sheet);

    return () => {
      const sheetToBeRemoved = document.getElementById('slider-styles');
      const sheetParent = sheetToBeRemoved?.parentNode;

      if (sheetParent) {
        sheetParent.removeChild(sheetToBeRemoved);
      }
    };
  }, [primaryColor]);

  const isSpanish = language === 'es';

  const confirmText = isSpanish ? 'Continuar' : 'Continue';
  const confirmDisabled = doNotAllowUnrelatedRegions;
  const cancelText = isSpanish ? 'Intentar nuevamente' : 'Try again';
  const modalTitle = doNotAllowUnrelatedRegions
    ? isSpanish
      ? 'No permitido'
      : 'Not allowed'
    : isSpanish
      ? '¿Estás seguro?'
      : 'Are you sure?';
  const modalDescription = doNotAllowUnrelatedRegions
    ? isSpanish
      ? 'Las áreas que seleccionaste no están relacionadas. Por favor, intenta de nuevo y elige áreas que formen parte de una misma área de queja. Puedes agregar quejas adicionales al final si es necesario.'
      : 'The regions you selected are not related. Please try again and select regions that are part of a single complaint area to proceed. You can add additional complaints at the end if needed.'
    : isSpanish
      ? 'Parece que algunas áreas que seleccionó no están relacionadas entre ellas. ¿Está seguro que desea continuar?'
      : 'It looks like you selected some areas that might not be related. Are you sure you want to continue?';

  const handleClose = () => {
    setSelected(defaultBodyParts);
    setConfirmContiguousModal(OpenClosedStates.Closed);
    setSourceOfPain({ side: null, x: null, y: null, reset: false });
  };

  return (
    <>
      <Transition
        show={show}
        // Opacity-only fade — DO NOT add `transform` / `scale` here.
        // CSS `transform` (even an identity `scale-100`) creates a
        // containing block for `position: fixed` descendants, which
        // would re-anchor the floating Rotate / Back / Next buttons
        // to this wrapper instead of the viewport and put them below
        // the fold whenever the survey page is scrollable.
        enter="transition-opacity ease-out duration-300"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="transition-opacity ease-in duration-300"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
        className="w-full"
      >
        <>
          <div className="flex-row justify-center hidden w-full gap-24 mx-auto lg:flex">
            <div className="text-xl font-medium text-gray-500 sm:text-2xl">
              <div
                className={cn(
                  'flex flex-col py-12',
                  myScreen
                    ? 'h-[800px] max-h-screen w-auto'
                    : // Cap by visible viewport so the picker doesn't extend
                      // under sticky portal chrome. Mobile budget covers
                      // the top header (~80px), bottom nav (~80px), the
                      // Back link row + page padding (~64px). Desktop has
                      // no bottom nav so the cap is much smaller.
                      'w-auto h-screen max-h-[calc(100dvh-16rem)] lg:max-h-[calc(100dvh-12rem)]'
                )}
              >
                <div className="relative flex flex-row justify-center w-auto h-full">
                  <div className="flex items-center w-auto h-full">
                    <span className="mt-24 text-4xl text-gray-700">
                      {language === 'es' ? 'I' : 'L'}
                    </span>
                  </div>
                  <svg
                    viewBox="0 0 405 1200"
                    className="body-picker"
                    version="1.1"
                    style={{
                      height: '100%',
                      width: 'auto',
                    }}
                    id="rear-desktop"
                  >
                    <image
                      width="405"
                      height="1200"
                      xlinkHref={maleBack}
                      style={{
                        height: '100%',
                        width: 'auto',
                      }}
                    />
                    {bodyParts[genderSelected].rear.map((part, i) => (
                      <path
                        key={i}
                        className={
                          selected?.rear?.[part.part]?.[part.leftOrRight] ? 'selected' : ''
                        }
                        onClick={e => {
                          onClickSourceOfPain('rear-desktop', 'rear', e);

                          if (!sourceOfPain.reset) {
                            selectPart('rear', part.part, part.leftOrRight);
                          }
                        }}
                        fill="transparent"
                        d={part.d}
                      />
                    ))}
                    {sourceOfPain?.side === 'rear' &&
                      typeof sourceOfPain.x === 'number' &&
                      typeof sourceOfPain.y === 'number' && (
                        <svg
                          x={sourceOfPain.x - 15}
                          y={sourceOfPain.y - 15}
                          width="30"
                          height="30"
                          viewBox="0 0 40 40"
                          xmlns="http://www.w3.org/2000/svg"
                          className="z-50 cursor-pointer"
                          onClick={repositionSourceOfPain}
                        >
                          <circle
                            cx="20"
                            cy="20"
                            fill="none"
                            r="10"
                            stroke="#f472b6"
                            strokeWidth="2"
                          >
                            <animate
                              attributeName="r"
                              from="8"
                              to="20"
                              dur="1.5s"
                              begin="0s"
                              repeatCount="indefinite"
                            />
                            <animate
                              attributeName="opacity"
                              from="1"
                              to="0"
                              dur="1.5s"
                              begin="0s"
                              repeatCount="indefinite"
                            />
                          </circle>
                          <circle cx="20" cy="20" fill="#f472b6" r="10" />
                        </svg>
                      )}
                  </svg>
                  <div className="flex items-center h-full">
                    <span className="mt-24 text-4xl text-gray-700">
                      {language === 'es' ? 'D' : 'R'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div className="text-xl font-medium text-gray-500 sm:text-2xl">
              <div
                className={cn(
                  'flex flex-col py-12',
                  myScreen
                    ? 'h-[800px] max-h-screen w-auto'
                    : // Cap by visible viewport so the picker doesn't extend
                      // under sticky portal chrome. Mobile budget covers
                      // the top header (~80px), bottom nav (~80px), the
                      // Back link row + page padding (~64px). Desktop has
                      // no bottom nav so the cap is much smaller.
                      'w-auto h-screen max-h-[calc(100dvh-16rem)] lg:max-h-[calc(100dvh-12rem)]'
                )}
              >
                <div className="relative flex flex-row justify-center w-auto h-full">
                  <div className="flex items-center w-auto h-full">
                    <span className="mt-24 text-4xl text-gray-700">
                      {' '}
                      {language === 'es' ? 'D' : 'R'}
                    </span>
                  </div>
                  <svg
                    viewBox="0 0 405 1200"
                    className="body-picker"
                    version="1.1"
                    style={{
                      height: '100%',
                      width: 'auto',
                    }}
                    id="front-desktop"
                  >
                    <image
                      width="405"
                      height="1200"
                      xlinkHref={maleFront}
                      style={{
                        height: '100%',
                        width: 'auto',
                      }}
                    />

                    {bodyParts[genderSelected].front.map((part, i) => (
                      <path
                        key={i}
                        className={
                          selected?.front?.[part.part]?.[part.leftOrRight] ? 'selected' : ''
                        }
                        onClick={e => {
                          onClickSourceOfPain('front-desktop', 'front', e);

                          if (!sourceOfPain.reset) {
                            selectPart('front', part.part, part.leftOrRight);
                          }
                        }}
                        fill="transparent"
                        d={part.d}
                      />
                    ))}
                    {sourceOfPain?.side === 'front' &&
                      typeof sourceOfPain.x === 'number' &&
                      typeof sourceOfPain.y === 'number' && (
                        <svg
                          x={sourceOfPain.x - 15}
                          y={sourceOfPain.y - 15}
                          width="30"
                          height="30"
                          viewBox="0 0 40 40"
                          xmlns="http://www.w3.org/2000/svg"
                          className="z-50 cursor-pointer"
                          onClick={repositionSourceOfPain}
                        >
                          <circle
                            cx="20"
                            cy="20"
                            fill="none"
                            r="10"
                            stroke="#f472b6"
                            strokeWidth="2"
                          >
                            <animate
                              attributeName="r"
                              from="8"
                              to="20"
                              dur="1.5s"
                              begin="0s"
                              repeatCount="indefinite"
                            />
                            <animate
                              attributeName="opacity"
                              from="1"
                              to="0"
                              dur="1.5s"
                              begin="0s"
                              repeatCount="indefinite"
                            />
                          </circle>
                          <circle cx="20" cy="20" fill="#f472b6" r="10" />
                        </svg>
                      )}
                  </svg>
                  <div className="flex items-center h-full">
                    <span className="mt-24 text-4xl text-gray-700">
                      {language === 'es' ? 'I' : 'L'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="flex flex-col items-center w-full lg:hidden">
            <div className="justify-center w-full max-w-4xl">
              <div className="flex flex-col">
                <div className="text-xl font-medium text-gray-500 sm:text-2xl">
                  <div
                    className={cn(
                      'flex flex-col py-12',
                      myScreen
                        ? 'h-[800px] max-h-screen w-auto'
                        : // Cap by visible viewport so the picker doesn't extend
                          // under sticky portal chrome. Mobile budget covers
                          // the top header (~80px), bottom nav (~80px), the
                          // Back link row + page padding (~64px). Desktop has
                          // no bottom nav so the cap is much smaller.
                          'w-auto h-screen max-h-[calc(100dvh-16rem)] lg:max-h-[calc(100dvh-12rem)]'
                    )}
                  >
                    <div className="relative flex flex-row justify-center w-auto h-full">
                      <div className="flex items-center w-auto h-full">
                        <span className="mt-24 text-4xl text-gray-700">
                          {sideSelected === 'front' ? 'R' : 'L'}
                        </span>
                      </div>
                      <svg
                        viewBox="0 0 405 1200"
                        className="body-picker"
                        version="1.1"
                        style={{
                          height: '100%',
                          width: 'auto',
                        }}
                        id="mobile"
                      >
                        <image
                          width="405"
                          height="1200"
                          xlinkHref={sideSelected === 'rear' ? maleBack : maleFront}
                          style={{
                            height: '100%',
                            width: 'auto',
                          }}
                        />

                        {bodyParts[genderSelected][sideSelected].map((part, i) => (
                          <path
                            key={i}
                            className={
                              selected?.[sideSelected]?.[part.part]?.[part.leftOrRight]
                                ? 'selected'
                                : ''
                            }
                            onClick={e => {
                              onClickSourceOfPain('mobile', sideSelected, e);

                              if (!sourceOfPain.reset) {
                                selectPart(sideSelected, part.part, part.leftOrRight);
                              }
                            }}
                            fill="transparent"
                            data-cy={i}
                            d={part.d}
                          />
                        ))}
                        {sourceOfPain?.side === sideSelected &&
                          typeof sourceOfPain.x === 'number' &&
                          typeof sourceOfPain.y === 'number' && (
                            <svg
                              x={sourceOfPain.x - 15}
                              y={sourceOfPain.y - 15}
                              width="30"
                              height="30"
                              viewBox="0 0 40 40"
                              xmlns="http://www.w3.org/2000/svg"
                              className="z-50 cursor-pointer"
                              onClick={repositionSourceOfPain}
                            >
                              <circle
                                cx="20"
                                cy="20"
                                fill="none"
                                r="10"
                                stroke="#f472b6"
                                strokeWidth="2"
                              >
                                <animate
                                  attributeName="r"
                                  from="8"
                                  to="20"
                                  dur="1.5s"
                                  begin="0s"
                                  repeatCount="indefinite"
                                />
                                <animate
                                  attributeName="opacity"
                                  from="1"
                                  to="0"
                                  dur="1.5s"
                                  begin="0s"
                                  repeatCount="indefinite"
                                />
                              </circle>
                              <circle cx="20" cy="20" fill="#f472b6" r="10" />
                            </svg>
                          )}
                      </svg>
                      <div className="flex items-center h-full">
                        <span className="mt-24 text-4xl text-gray-700">
                          {sideSelected === 'front' ? 'L' : 'R'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Rotate (mobile only — desktop renders both views side-by-side
              so there's nothing to flip). Pulled out of the picker
              container so it shares the viewport reference frame with
              Next/Back and visually aligns with them above the fixed
              bottom nav. Shifts right when the in-survey back button
              also occupies bottom-left. `fixed` (not `absolute`) so
              the button stays anchored to the viewport even when the
              survey page scrolls — `absolute` anchors to the document
              and would land below the fold once the page is taller
              than the viewport. */}
          <button
            onClick={turnOver}
            className={cn(
              'fixed z-10 lg:hidden items-center justify-center px-5 py-3 text-base font-medium transition duration-150 ease-in-out border border-gray-300 rounded-md shadow cursor-pointer hover:opacity-90 focus:outline-none focus:ring bg-white bottom-[calc(4rem+env(safe-area-inset-bottom))]',
              goBack && id !== CCSBodyPickerQuestionIds.primary ? 'left-32' : 'left-4'
            )}
            data-cy="rotate-button"
          >
            {questionContentVerbiage[language === 'es' ? 'es' : 'en'].rotate}
          </button>

          {goBack && id !== CCSBodyPickerQuestionIds.primary && (
            <button
              onClick={goBack}
              className="fixed items-center justify-center px-5 py-3 text-base font-medium transition duration-150 ease-in-out border border-gray-300 rounded-md shadow cursor-pointer left-4 bottom-[calc(4rem+env(safe-area-inset-bottom))] lg:bottom-4 hover:opacity-90 focus:outline-none focus:ring bg-white z-10"
              data-cy="back-button"
            >
              <div className="flex items-center text-center">
                <svg
                  className="w-5 h-5"
                  style={{
                    color: primaryColor,
                  }}
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="text-gray-500">
                  {questionContentVerbiage[language === 'es' ? 'es' : 'en'].back}
                </span>
              </div>
            </button>
          )}
          <button
            onClick={onSubmit}
            disabled={disabled}
            // Mirror the back button's `bottom-[calc(4rem+env(safe-area-inset-bottom))] lg:bottom-4` so the
            // primary action clears the mobile portal's fixed bottom
            // nav (~80px tall) without floating away from the picker
            // on desktop.
            className="fixed items-center justify-center px-5 py-3 text-base font-medium text-white transition duration-150 ease-in-out border border-transparent rounded-md shadow cursor-pointer right-4 bottom-[calc(4rem+env(safe-area-inset-bottom))] lg:bottom-4 hover:opacity-90 focus:outline-none focus:ring z-10"
            style={{
              backgroundColor: primaryColor,
            }}
            data-cy="next-button"
          >
            {questionContentVerbiage[language === 'es' ? 'es' : 'en'].next}
          </button>
        </>
      </Transition>
      <ConfirmModal
        isOpen={confirmContiguousModal === OpenClosedStates.Open}
        confirm={() => (doNotAllowUnrelatedRegions ? undefined : submit())}
        close={handleClose}
        confirmText={confirmText}
        cancelText={cancelText}
        title={modalTitle}
        description={modalDescription}
        icon={<QuestionMarkCircleIcon className="w-6 h-6 text-gray-600 dark:text-dark-gray-400" />}
        primaryColor={primaryColor}
        hideConfirm={doNotAllowUnrelatedRegions}
      />
    </>
  );
};

export default Body;
